import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useApiConfigStore } from "@store/api_config";

interface QueueItem {
  id: string;
  brand: string;
  model: string;
  color: string;
  status: string;
}

interface QueueResponse {
  batch: QueueItem[];
  stats: { total: number; done: number; pending: number };
}

export default function WatermarkScreen() {
  const getApiUrl = useApiConfigStore((s) => s.getApiUrl);
  const [queue, setQueue] = useState<QueueResponse | null>(null);
  const [uploading, setUploading] = useState<string | null>(null);
  const [done, setDone] = useState<Set<string>>(new Set());

  const fetchQueue = useCallback(async () => {
    const { data } = await axios.get<QueueResponse>(`${getApiUrl()}/watermark/queue`);
    setQueue(data);
  }, [getApiUrl]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  const handleDownload = async (item: QueueItem) => {
    const { data } = await axios.get(`${getApiUrl()}/watermark/download`, {
      params: { brand: item.brand, model: item.model, color: item.color },
    });
    window.open(data.url, "_blank");
  };

  const handleUpload = async (item: QueueItem, file: File) => {
    setUploading(item.id);
    try {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("brand", item.brand);
      formData.append("model", item.model);
      formData.append("color", item.color);
      formData.append("doc_id", item.id);

      await axios.post(`${getApiUrl()}/watermark/upload`, formData);
      setDone((prev) => new Set(prev).add(item.id));
    } finally {
      setUploading(null);
    }
  };

  if (!queue) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-gray-400">Chargement...</p>
      </div>
    );
  }

  const { stats, batch } = queue;
  const progress = stats.total > 0 ? Math.round((stats.done / stats.total) * 100) : 0;

  return (
    <div className="min-h-screen bg-gray-50 p-8 max-w-3xl mx-auto">
      <h1 className="text-2xl font-bold mb-2">Watermark Removal</h1>
      <div className="flex items-center gap-4 mb-6">
        <div className="flex-1 bg-gray-200 rounded-full h-3">
          <div
            className="bg-[#5C6BFF] h-3 rounded-full transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className="text-sm text-gray-500 whitespace-nowrap">
          {stats.done}/{stats.total} ({progress}%)
        </span>
      </div>

      <p className="text-sm text-gray-500 mb-6">
        {stats.pending} images restantes. Lot actuel : {batch.length} images.
      </p>

      <div className="space-y-3">
        {batch.map((item) => {
          const isDone = done.has(item.id);
          const isUploading = uploading === item.id;
          return (
            <div
              key={item.id}
              className={`flex items-center justify-between bg-white rounded-xl border p-4 ${
                isDone ? "border-green-200 bg-green-50" : "border-gray-100"
              }`}
            >
              <div>
                <span className="font-medium text-gray-800 capitalize">
                  {item.brand} {item.model}
                </span>
                <span className="ml-2 text-sm text-gray-400">{item.color}</span>
              </div>

              <div className="flex items-center gap-2">
                {isDone && (
                  <span className="text-green-600 text-sm font-medium mr-1">Fait</span>
                )}
                <button
                  onClick={() => handleDownload(item)}
                  className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition"
                >
                  Télécharger
                </button>
                <label
                  className={`px-3 py-1.5 text-sm text-white rounded-lg cursor-pointer transition ${
                    isUploading
                      ? "bg-gray-400 cursor-not-allowed"
                      : isDone
                        ? "bg-orange-500 hover:bg-orange-600"
                        : "bg-[#5C6BFF] hover:bg-[#4a59e6]"
                  }`}
                >
                  {isUploading ? "..." : isDone ? "Remplacer" : "Upload"}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={isUploading}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleUpload(item, f);
                    }}
                  />
                </label>
              </div>
            </div>
          );
        })}
      </div>

      {batch.length === 0 && (
        <div className="text-center py-16">
          <p className="text-lg font-medium text-gray-600">Toutes les images sont traitées !</p>
        </div>
      )}
    </div>
  );
}
